import MuxPlayer, { MuxPlayerRefAttributes } from '@mux/mux-player-react';
import { isAxiosError } from 'axios';
import { useLocalSearchParams, useNavigation } from 'expo-router';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { StyleSheet, View } from 'react-native';

import { useAuthenticateEpisode } from '@/api/hooks/showHooks';
import GoBackButton from '@/components/buttons/GoBackButton';
import DotsLoader from '@/components/DotsLoader';
import { CHROMECAST_APPLICATION_ID, MAX_RETRIES } from '@/constants/config';
import { useEndFlowModal } from '@/contexts/endFlowModalContext';
import { AppStorage } from '@/utils/AppStorage';
import { clearEpisodeProgress, getEpisodeProgress, saveEpisodeProgress } from '@/utils/showEpisodesUtils';

const Episode = () => {
  const navigation = useNavigation();
  const params = useLocalSearchParams();
  const episodeId = typeof params.episodeId === 'string' ? params.episodeId : undefined;

  const { showError } = useEndFlowModal();

  const { mutateAsync: authenticateEpisode, isPending: authenticatingEpisode, failureCount } = useAuthenticateEpisode();

  const [playerData, setPlayerData] = useState<{ playbackId: string; token: string; drmToken?: string }>();
  const [currentTime, setCurrentTime] = useState(0);
  const [initialTime, setInitialTime] = useState(0);

  const muxRef = useRef<MuxPlayerRefAttributes>(null);
  const lastSentRef = useRef<number>(0);

  const onAuthenticate = async () => {
    while (failureCount < MAX_RETRIES) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const data = await authenticateEpisode({
          episodeId: episodeId ?? ''
        });
        // eslint-disable-next-line no-await-in-loop
        const lastTimeStamp = await getEpisodeProgress(episodeId ?? '');
        setInitialTime(lastTimeStamp ?? 0);
        setPlayerData(data);
        return;
      } catch (e) {
        console.error(e);
        if (isAxiosError(e) && (e.response?.status === 409 || e.status === 409)) {
          continue;
        }

        showError({});
        console.error('Unknown error:', e);
        break;
      }
    }
  };

  useEffect(() => {
    onAuthenticate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [episodeId]);

  useLayoutEffect(() => {
    navigation.setOptions({
      headerShown: false
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const player = muxRef.current;
    if (player && currentTime > 0) {
      player.currentTime = currentTime;
      player.play();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerData]);

  const onEnded = () => {
    const player = muxRef.current;
    if (!player) return;

    if (episodeId) {
      clearEpisodeProgress(episodeId);
      AppStorage.setItem('lastEpisodeCompleted', episodeId);
    }
  };

  const onTimeUpdate = () => {
    const player = muxRef.current;
    if (!player) return;
    const now = Date.now();

    if (now - lastSentRef.current >= 10000) {
      lastSentRef.current = now;
      if (episodeId) saveEpisodeProgress(episodeId, player.currentTime);
    }
  };

  return (
    <View style={styles.container}>
      <GoBackButton iconColor="white" style={styles.goBack} />
      {playerData === undefined || authenticatingEpisode ? (
        <DotsLoader color="white" />
      ) : (
        <MuxPlayer
          ref={muxRef}
          style={styles.muxPlayer}
          theme="classic"
          playbackId={playerData.playbackId}
          tokens={{
            playback: playerData.token,
            drm: playerData.drmToken
          }}
          castReceiver={CHROMECAST_APPLICATION_ID}
          autoPlay
          startTime={initialTime}
          onEnded={onEnded}
          onTimeUpdate={onTimeUpdate}
          onError={(event: any) => {
            const error = event as any;
            const muxError = error.detail;
            if (muxError.data.error.message.includes('403') || muxError.includes('token')) {
              console.warn('MUX token may have expired. Fetching new one...');

              const time = muxRef.current?.currentTime || 0;
              setCurrentTime(time);

              onAuthenticate();
            } else {
              console.error('MUX Player error', error);
            }
          }}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center'
  },

  goBack: {
    position: 'absolute',
    top: 15,
    left: 15,
    zIndex: 99
  },

  muxPlayer: {
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: '100%',
    overflow: 'hidden'
  }
});

export default Episode;
